import React, {Fragment} from 'react';
import {
  Checkbox,
  createStyles,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  makeStyles
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {FONT_TERTIARY_COLOR, INPUT_LABEL_COLOR} from 'src/style/variable';

interface IOptionItem {
  value: string | number;
  label: string;
  option_group?: string;
}
interface CheckboxGroupFieldProps {
  options: IOptionItem[];
  label: string;
  error?: Record<string, any>;
  required?: boolean;
  value: (string | number)[];
  onChange: (value: any) => void;
  name: string;
  fieldStyleOptions?: any;
}

const useStyles = makeStyles(() => {
  return createStyles({
    formControlLabel: {
      margin: 0,
      '& .MuiButtonBase-root': {
        padding: '0px',
        paddingRight: '0.5rem'
      },
      '& .MuiFormControlLabel-label': {
        marginTop: '0.3rem',
        fontSize: '1.1rem',
        color: INPUT_LABEL_COLOR
      }
    },
    formControl: (props: any) => ({
      paddingBottom: 0,
      marginBottom: 0,
      '& .option-group-label': {
        color: FONT_TERTIARY_COLOR,
        fontSize: '1.1rem',
        marginBottom: '0.5rem'
      },
      ...(props?.display === 'grid' && props?.gridMinColumnWidth
        ? {
            '& .MuiFormGroup-root': {
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${props.gridMinColumnWidth}, 1fr))`,
              columnGap: '0.25rem'
            }
          }
        : {})
    })
  });
});

const CheckboxGroupField = (props: CheckboxGroupFieldProps): JSX.Element => {
  const {
    label,
    error,
    required,
    options,
    fieldStyleOptions,
    value,
    onChange,
    name
  } = props;
  const {t} = useTranslation();
  const classes = useStyles({
    display: fieldStyleOptions?.display,
    gridMinColumnWidth: fieldStyleOptions?.gridMinColumnWidth
  });

  let checkBoxValue = [];
  if (Array.isArray(value)) {
    checkBoxValue = value;
  }

  const handleCheckboxChange = (item: IOptionItem) => {
    const currentIndex = checkBoxValue.indexOf(item.value);
    const updatedValue = [...checkBoxValue];

    if (currentIndex === -1) {
      updatedValue.push(item.value);
    } else {
      updatedValue.splice(currentIndex, 1);
    }
    onChange(updatedValue);
  };

  const renderOptions = () => {
    const optionGroupMapper = new Map();
    // all the option must have option_group or none of them should have option_group
    if (options.every(o => o.option_group)) {
      options.forEach(o => {
        if (optionGroupMapper.has(o.option_group)) {
          optionGroupMapper.set(o.option_group, [
            ...optionGroupMapper.get(o.option_group),
            o
          ]);
        } else {
          optionGroupMapper.set(o.option_group, [o]);
        }
      });

      return Array.from(optionGroupMapper).map(
        ([optionGroup, optionItems], idx) => {
          return (
            <Fragment key={optionGroup}>
              <p
                className="option-group-label"
                style={{
                  marginTop: idx === 0 ? 0 : '0.5rem'
                }}>
                {optionGroup}
              </p>
              <FormGroup>
                {optionItems.map((o: IOptionItem) => (
                  <FormControlLabel
                    className={classes.formControlLabel}
                    key={o.value}
                    control={
                      <Checkbox
                        checked={checkBoxValue.includes(o.value)}
                        name={name}
                        onChange={() => {
                          handleCheckboxChange(o);
                        }}
                        {...o}
                      />
                    }
                    label={o.label}
                  />
                ))}
              </FormGroup>
            </Fragment>
          );
        }
      );
    }
    return (
      <FormGroup>
        {options.map(o => (
          <FormControlLabel
            className={classes.formControlLabel}
            key={o.value}
            control={
              <Checkbox
                checked={checkBoxValue.includes(o.value)}
                name={name}
                onChange={() => {
                  handleCheckboxChange(o);
                }}
                {...o}
              />
            }
            label={o.label}
          />
        ))}
      </FormGroup>
    );
  };

  return (
    <FormControl
      component="fieldset"
      required={required}
      className={classes.formControl}
      error={!!error}>
      <InputLabel
        style={{
          position: 'static',
          transform: 'none',
          color: INPUT_LABEL_COLOR,
          fontSize: '1.1rem',
          marginBottom: '0.5rem'
        }}
        shrink>
        {label}
      </InputLabel>
      {renderOptions()}
      {error && <FormHelperText>{t(error.message)}</FormHelperText>}
    </FormControl>
  );
};

export default CheckboxGroupField;
